import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  limit,
} from 'firebase/firestore';
import {
  db,
  updatePlayerDetails,
  getPlayers,
  upsertPlayerPublic,
} from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import PlayerModal from './PlayerModal';
import './PlayerActivity.css';

function PlayerActivity() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [playerName, setPlayerName] = useState('Player');
  const [avatar, setAvatar] = useState('🐶');
  const [totalPoints, setTotalPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [privatePlayer, setPrivatePlayer] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        // Public profile
        const pubRef = doc(db, 'playerPublic', playerId);
        const pubSnap = await getDoc(pubRef);
        if (!pubSnap.exists()) {
          setNotFound(true);
          return;
        }
        const pubData = pubSnap.data();
        setPlayerName(pubData.displayName || 'Player');
        setAvatar(pubData.avatar || '🐶');
        setTotalPoints(pubData.totalPoints || 0);

        // Public events feed
        const eventsRef = collection(db, 'playerPublic', playerId, 'events');
        const q = query(eventsRef, orderBy('completedAt', 'desc'), limit(50));
        const eventsSnap = await getDocs(q);
        const items = eventsSnap.docs.map((d) => {
          const e = d.data();
          const ts = e.completedAt?.toDate?.() || new Date();
          const label =
            e.title ||
            (e.type === 'workout'
              ? `Workout ${e.id}`
              : e.type === 'skill'
              ? `Skill ${e.id}`
              : `Exercise ${e.id}`);
          return {
            type: e.type,
            id: e.id,
            label,
            points: e.points ?? null,
            completedAt: ts,
          };
        });
        setEntries(items);

        // If user is logged in, determine ownership from global mapping
        if (user) {
          const idRef = doc(db, 'playerIds', playerId);
          const idSnap = await getDoc(idRef);
          const data = idSnap.exists() ? idSnap.data() : null;
          const ownerId = data?.userId;
          const isOwnerOfProfile = ownerId && ownerId === user.uid;
          setIsOwner(Boolean(isOwnerOfProfile));

          if (isOwnerOfProfile) {
            // Load private player to get the Firestore doc id for updates
            const list = await getPlayers(user.uid);
            const match = list.find((p) => p.playerId === playerId);
            if (match) setPrivatePlayer(match);
          }
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [playerId, user]);

  if (loading) return <div>Loading…</div>;
  if (notFound)
    return (
      <div className='profile-content'>
        <h2>Profile not found</h2>
        <p>This player does not have a public profile yet.</p>
      </div>
    );

  return (
    <div className='profile-page'>
      <div className='profile-header'>
        <button
          className='back-button profile-back'
          onClick={() => navigate('/dashboard')}
        >
          ← Back
        </button>
        {isOwner && (
          <button
            className='edit-button profile-edit'
            onClick={() => setShowEdit(true)}
          >
            Edit
          </button>
        )}
        <div className='profile-header-content'>
          <span className='profile-avatar'>{avatar}</span>
          <div className='profile-name-container'>
            <div className='profile-name'>{playerName}</div>
            <div className='profile-points'>{totalPoints} points</div>
          </div>
        </div>
      </div>
      <div className='profile-content'>
        <div className='activity-title'>Activity</div>
        <ul className='activity-list'>
          {entries.map((e, idx) => (
            <li key={idx} className='activity-item'>
              <span className='activity-icon'>
                {e.type === 'workout' ? '🏋️' : e.type === 'skill' ? '🎯' : '💪'}
              </span>
              <div className='activity-body'>
                <div className='activity-label'>{e.label}</div>
                <div className='activity-date'>
                  {e.completedAt.toLocaleString()}
                </div>
              </div>
            </li>
          ))}
          {entries.length === 0 && (
            <li className='activity-empty'>No activity yet.</li>
          )}
        </ul>
      </div>

      {/* Owner-only Edit Modal */}
      {isOwner && privatePlayer && (
        <PlayerModal
          isOpen={showEdit}
          mode='edit'
          isSubmitting={saving}
          initialName={privatePlayer.playerName || playerName}
          initialAge={privatePlayer.age}
          initialEmoji={privatePlayer.avatar || avatar}
          onClose={() => setShowEdit(false)}
          onSubmit={async ({ name, age, emoji }) => {
            try {
              setSaving(true);
              await updatePlayerDetails(
                user.uid,
                privatePlayer.id,
                name,
                emoji,
                age
              );
              // Update public mirror and global mapping for immediate consistency
              await upsertPlayerPublic({
                playerId,
                displayName: name,
                avatar: emoji,
                totalPoints,
              });
              // Also update global playerIds display name and avatar
              const pidRef = doc(db, 'playerIds', playerId);
              await import('firebase/firestore').then(({ setDoc }) =>
                setDoc(
                  pidRef,
                  { displayName: name, avatar: emoji },
                  { merge: true }
                )
              );
              setShowEdit(false);
              // Reflect changes in local UI immediately
              setPlayerName(name);
              setAvatar(emoji);
            } finally {
              setSaving(false);
            }
          }}
        />
      )}
    </div>
  );
}

export default PlayerActivity;
